import HotelPlaceholderImage from 'assets/hotel-placeholder.svg';
import classNames from 'classnames';
import { FC } from 'react';
import { twMerge } from 'tailwind-merge';

import { AddOrRemoveFromShortListButton } from 'components/AddOrRemoveFromShortListButton';
import Image from 'components/Image';
import PriceTag from 'components/PriceTag';
import { HotelSearchQuery } from 'generated/graphql';
import { useAppSelector } from 'store/store';
import getEnumLabel from 'utilities/getEnumLabel';

type HotelSearchResultProps = {
  result: HotelSearchQuery['hotelSearch']['results'][number];
  bookingType: 'instant' | 'group';
  onClick?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  isHighlighted?: boolean;
};

const HotelSearchResult: FC<HotelSearchResultProps> = ({
  result: { hotel, offers },
  onClick,
  onMouseEnter,
  onMouseLeave,
  isHighlighted,
}) => {
  const { amenities } = useAppSelector((state) => state.enums);
  const bookingType = useAppSelector((state) => state.booking.bookingType);

  return (
    <div
      className={twMerge(
        classNames(
          'm-4 flex cursor-pointer rounded-[10px] border border-gray-200 transition-[background-color] duration-200 sm:max-h-36 flex-col sm:flex-row',
          'hover:shadow-md focus:ring-2 focus:ring-accentGreen',
          {
            'bg-lightGreen': isHighlighted,
            'bg-gray-50': !isHighlighted,
          },
        ),
      )}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onClick?.();
        }
      }}
    >
      <div className="shrink-0">
        <Image
          src={hotel?.imageUrl || HotelPlaceholderImage}
          alt={hotel.name}
          className="h-48 w-full rounded-t-md object-cover sm:size-[140px] sm:rounded-l-md sm:rounded-t-none"
        />
      </div>
      <div className="flex grow flex-col justify-between px-[10px] py-4">
        <div>
          <div className="mb-2 flex items-center gap-2">
            <p className="font-oswald text-lg font-medium">{hotel.name}</p>
          </div>
          <p className="text-sm text-primaryGray xl:text-base">
            {hotel.formattedAddress}
          </p>
        </div>
        <div className="mt-2 sm:mt-0">
          {hotel.amenities && hotel.amenities.length > 0 && (
            <ul className="flex flex-wrap gap-2 text-sm text-accentBlue sm:h-5">
              {hotel.amenities.slice(0, 3).map((amenity, index) => (
                <li
                  key={index}
                  className="rounded-[4px] bg-lightBlue px-2 py-1 sm:py-0"
                >
                  {getEnumLabel(amenities, amenity) || amenity}
                </li>
              ))}
              {hotel.amenities.length > 3 && (
                <li className="rounded-[4px] bg-lightBlue px-2 py-1 sm:py-0">
                  +{hotel.amenities.length - 3} more
                </li>
              )}
            </ul>
          )}
        </div>
      </div>
      <div className="shrink-0 border-t p-4 sm:min-w-40 sm:border-l sm:border-t-0 sm:p-[10px]">
        {bookingType === 'instant' ? (
          <PriceTag
            key={`${hotel.id}-${offers[0].bookingCode}`}
            offer={offers[0]}
            thsPrice={offers[0].thsPrice}
          />
        ) : (
          <AddOrRemoveFromShortListButton hotel={hotel} />
        )}
      </div>
    </div>
  );
};

export default HotelSearchResult;
